import type { ReactNode } from "react"
import { Link } from "react-router"
import { ChevronRightIcon } from "lucide-react"

import { Skeleton } from "@/components/ui/skeleton"

export interface Crumb {
  labelKey?: string
  /** Resolved from the loaded record. Undefined while loading. */
  label?: string
  to?: string
}

/**
 * Title, breadcrumb and actions in one consistent place, so an operator's hand
 * learns one location for the action they want (LLD §8.3).
 *
 * A crumb with no label yet renders a SKELETON, never a raw uuid -- not even
 * briefly (FR-DSH-SHL-03).
 */
export function PageHeader({
  title,
  crumbs = [],
  status,
  actions,
  description,
}: {
  title: ReactNode
  crumbs?: Crumb[]
  status?: ReactNode
  actions?: ReactNode
  description?: ReactNode
}) {
  return (
    <header className="mb-6 flex flex-col gap-3">
      {crumbs.length > 0 ? (
        <nav aria-label="Breadcrumb">
          <ol className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
            {crumbs.map((crumb, i) => (
              <li key={crumb.to ?? i} className="flex items-center gap-1">
                {i > 0 ? <ChevronRightIcon className="size-3" aria-hidden /> : null}
                {crumb.label === undefined ? (
                  <Skeleton className="h-3 w-24" />
                ) : crumb.to ? (
                  <Link to={crumb.to} className="hover:text-foreground">
                    {crumb.label}
                  </Link>
                ) : (
                  <span>{crumb.label}</span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      ) : null}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="truncate text-xl font-semibold tracking-tight">{title}</h1>
            {status}
          </div>
          {description ? <p className="mt-1 text-sm text-muted-foreground">{description}</p> : null}
        </div>
        {/* Wraps, and never wider than the page: four actions on a phone go to two rows. */}
        {actions ? (
          <div className="flex max-w-full flex-wrap items-center gap-2">{actions}</div>
        ) : null}
      </div>
    </header>
  )
}
